import { useEffect, useRef } from "react";
import { createChart, ColorType } from "lightweight-charts";
import { NseChartEngine } from "@/lib/NseChartEngine";

/**
 * ChartCanvas — owns the lightweight-charts instance + the render pipeline.
 * Parent supplies `bars`, `chartType`, and `tier`; the canvas draws them.
 * Reports render errors back via `onRenderError`.
 */
export default function ChartCanvas({ bars, chartType, tier, indicators, riskLevels, height = 380, onRenderError }) {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const engineRef = useRef(null);

    // Create chart + engine on mount; dispose on unmount.
    useEffect(() => {
        if (!containerRef.current) return;
        const chart = createChart(containerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: "transparent" },
                textColor: "hsl(215.4 16.3% 46.9%)",
                // Design system directive (2026-09): axis labels/price ticks are
                // drawn by lightweight-charts directly onto a <canvas> element —
                // Canvas 2D's font string has NO font-feature-settings support,
                // so any monospace code font (SF Mono/Menlo/Consolas) renders its
                // baked-in SLASHED zero glyph with no way to override it via CSS.
                // Inter's default zero is open/proportional with no toggle needed.
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                fontSize: 11,
                // Hide the lightweight-charts "TV" attribution watermark
                // (external TradingView branding) — the platform is
                // Melian-Dialogue-branded end-to-end.
                attributionLogo: false,
            },
            grid: {
                vertLines: { color: "rgba(120,120,120,0.10)" },
                horzLines: { color: "rgba(120,120,120,0.10)" },
            },
            rightPriceScale: { borderColor: "rgba(120,120,120,0.20)" },
            timeScale:       { borderColor: "rgba(120,120,120,0.20)" },
            autoSize: true,
        });
        chartRef.current = chart;
        engineRef.current = new NseChartEngine(chart);

        const ro = new ResizeObserver(() => {
            if (!chartRef.current || !containerRef.current) return;
            chart.applyOptions({
                width: containerRef.current.clientWidth,
                height: containerRef.current.clientHeight,
            });
        });
        ro.observe(containerRef.current);

        return () => {
            ro.disconnect();
            try { engineRef.current?.destroy(); } catch (e) { /* series already removed */ }
            try { chart.remove(); } catch (e) { /* ignore */ }
            chartRef.current = null;
            engineRef.current = null;
        };
    }, []);

    // Re-render whenever bars / type / tier change.
    useEffect(() => {
        const engine = engineRef.current;
        if (!engine || !bars || bars.length === 0) return;
        try {
            engine.renderChart(chartType, bars, tier);
            engine.setRiskLevels(riskLevels || {});
            onRenderError?.(null);
        } catch (e) {
            onRenderError?.(e.message || String(e));
        }
    }, [bars, chartType, tier, onRenderError]);

    // Stop-loss / take-profit / entry guide lines — re-applied independently
    // so dragging the SL/TP input doesn't re-render the whole chart series.
    useEffect(() => {
        const engine = engineRef.current;
        if (!engine || !engine.activeSeries) return;
        engine.setRiskLevels(riskLevels || {});
    }, [riskLevels]);

    // Indicator overlays/sub-panes are independent of chart type — re-apply
    // whenever the bars or the toggled indicator set change.
    useEffect(() => {
        const engine = engineRef.current;
        if (!engine || !bars || bars.length === 0) return;
        try {
            engine.applyIndicators(bars, indicators || {});
        } catch (e) {
            // Indicators are a bonus overlay — never block the primary chart.
        }
    }, [bars, indicators]);

    return (
        <div className="relative w-full" style={{ height }}>
            <div ref={containerRef} className="absolute inset-0" data-testid="advanced-chart-container" />
        </div>
    );
}
